import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ProjectCard } from '../components/ProjectCard'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../lib/api'
import type { ProjectSummary } from '../lib/project'

export function ExplorePage() {
  const location = useLocation()
  const { user } = useAuth()
  const [query, setQuery] = useState('')
  const [projects, setProjects] = useState<ProjectSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [sort, setSort] = useState<'updated' | 'favorites' | 'views'>('updated')
  const [period, setPeriod] = useState<'day' | 'week' | 'month'>('day')
  const [updating, setUpdating] = useState(false)
  const hasSuccessfulResults = useRef(false)
  const restored = useRef(false)
  const latestRequest = useRef(0)
  const load = useCallback((keepResults = false) => {
    const request = latestRequest.current + 1
    latestRequest.current = request
    if (keepResults) setUpdating(true); else setLoading(true)
    setFailed(false)
    void api<ProjectSummary[]>(sort === 'updated' ? '/api/public-projects' : `/api/public-projects?sort=${sort}&period=${period}`)
      .then((nextProjects) => {
        if (request !== latestRequest.current) return
        hasSuccessfulResults.current = true
        setProjects(nextProjects)
      })
      .catch(() => {
        if (request === latestRequest.current) setFailed(true)
      })
      .finally(() => {
        if (request === latestRequest.current) {
          setLoading(false)
          setUpdating(false)
        }
      })
  }, [period, sort])

  useEffect(() => {
    const saved = (location.state as { exploreRestore?: { query: string; sort: 'updated' | 'favorites' | 'views'; period: 'day' | 'week' | 'month'; projects: ProjectSummary[]; scrollY: number; shareSlug: string } } | null)?.exploreRestore
    if (!restored.current && saved && Array.isArray(saved.projects)) {
      restored.current = true; hasSuccessfulResults.current = true
      setQuery(saved.query); setSort(saved.sort); setPeriod(saved.period); setProjects(saved.projects); setLoading(false)
      window.requestAnimationFrame(() => { window.scrollTo(0, saved.scrollY); document.querySelector<HTMLElement>(`[data-explore-card="${saved.shareSlug}"]`)?.focus() })
      return
    }
    load(hasSuccessfulResults.current)
  }, [load, location.state])

  const rememberExplore = (shareSlug: string) => {
    const state = { query, sort, period, projects, scrollY: window.scrollY, shareSlug }
    window.history.replaceState({ ...window.history.state, usr: { ...(window.history.state?.usr ?? {}), exploreRestore: state } }, '')
  }

  const lowered = query.trim().toLowerCase()
  const filtered = useMemo(
    () => projects.filter(
      (project) => !lowered || `${project.youtubeTitle ?? project.title} ${project.youtubeChannelTitle ?? ''} ${project.creatorName ?? ''}`.toLowerCase().includes(lowered),
    ),
    [lowered, projects],
  )
  const createPath = user ? '/projects/new' : '/login'
  const refreshFailed = failed && projects.length > 0

  return (
    <div className="page explore-page">
      <header className="explore-hero">
        <span className="eyebrow">公開編排</span>
        <h1>找一首歌，從你要的段落開始</h1>
        <p>瀏覽大家公開的歌曲編排；開啟後可直接播放，並選擇下一段。</p>
        <form className="explore-actions" role="search" onSubmit={(event) => event.preventDefault()}>
          <label className="search-field search-field--entertainment">
            <span>搜尋公開作品</span>
            <span className="search-input">
              <span className="search-icon" aria-hidden="true" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="搜尋歌曲、頻道或創作者"
              />
            </span>
          </label>
          <div className="explore-switches">
            <div className="sort-group" role="group" aria-labelledby="explore-sort-label">
              <span className="sort-group__label" id="explore-sort-label">排序方式</span>
              <div className="segmented-control">
                {([['updated', '最新更新'], ['favorites', '收藏數'], ['views', '播放次數']] as const).map(([value, label]) => (
                  <button key={value} className="segmented-control__button" type="button" aria-pressed={sort === value} onClick={() => setSort(value)}>{label}</button>
                ))}
              </div>
            </div>
            {sort !== 'updated' && <div className="sort-group" role="group" aria-labelledby="explore-period-label">
              <span className="sort-group__label" id="explore-period-label">時間範圍</span>
              <div className="segmented-control">
                {([['day', '今天'], ['week', '本週'], ['month', '本月']] as const).map(([value, label]) => (
                  <button key={value} className="segmented-control__button" type="button" aria-pressed={period === value} onClick={() => setPeriod(value)}>{label}</button>
                ))}
              </div>
            </div>}
          </div>
          <Link className="button button--large" to={createPath}>
            {user ? '建立作品' : '登入後建立作品'}
          </Link>
        </form>
      </header>

      {loading && (
        <section className="content-section public-projects" aria-label="公開作品">
          <h2>公開作品</h2>
          <p className="sr-only" role="status">正在讀取公開作品…</p>
          <div className="project-grid project-grid--loading" aria-hidden="true">
            {[0, 1, 2].map((index) => (
              <div className="project-skeleton" key={index}>
                <span />
                <i />
                <i />
              </div>
            ))}
          </div>
        </section>
      )}

      {failed && !refreshFailed && (
        <div className="empty-state empty-state--error" role="alert">
          <div>
            <h2>公開作品暫時無法載入</h2>
            <p>目前無法載入公開作品，請重新整理後再試。</p>
          </div>
          <button className="button button--primary" type="button" onClick={() => load()}>重新整理</button>
        </div>
      )}

      {!loading && (!failed || refreshFailed) && filtered.length > 0 && (
        <section className="content-section public-projects">
          <h2>公開作品 {sort !== 'updated' && <small>依{period === 'day' ? '今天' : period === 'week' ? '本週' : '本月'}{sort === 'views' ? '播放' : '收藏'}數排序</small>} {updating && <span role="status">正在更新排序…</span>}</h2>
          {refreshFailed && (
            <div className="explore-refresh-error" role="alert">
              <span>無法更新排序，已顯示先前結果。</span>
              <button className="button" type="button" onClick={() => load(true)}>重試</button>
            </div>
          )}
          <div className="project-grid">
            {filtered.map((project) => <ProjectCard project={project} key={project.id} fromExplore onExploreNavigate={rememberExplore} />)}
          </div>
        </section>
      )}

      {!loading && !failed && projects.length > 0 && filtered.length === 0 && (
        <div className="empty-state empty-state--search">
          <div>
            <h2>找不到符合的公開作品</h2>
            <p>換一個歌曲、頻道或創作者名稱再試一次。</p>
          </div>
          <button className="button" type="button" onClick={() => setQuery('')}>清除搜尋</button>
        </div>
      )}

      {!loading && !failed && projects.length === 0 && (
        <div className="empty-state entertainment-empty">
          <div className="empty-media">
            <img
              src="/assets/ilovesing-segment-stage-v1.png"
              alt="抽象段落舞台，四個歌曲段落沿著路徑排列"
            />
            <span className="empty-media__shade" aria-hidden="true" />
            <span className="empty-media__play" />
          </div>
          <div>
            <h2>第一個公開編排，從你開始</h2>
            <p>貼上可嵌入的 YouTube 影片，標記段落後即可分享給大家練唱。</p>
            <Link className="button button--primary" to={createPath}>
              {user ? '建立第一個作品' : '登入後建立第一個作品'}
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
